import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTRPC } from "../../lib/api";
import { Button, ErrorText, TextField } from "../../ui";
import { useToast } from "../../ui/toast";
import { googleAccounts, isScopesMissing, meetEnabled, meetErrorText, type Account } from "./model";
import { ScheduleForm } from "./ScheduleForm";

/** A tab opened from the click (so popup blockers allow it) and pointed at the link later. */
export interface PendingTab {
  go(url: string): void;
  close(): void;
}

export function blankTab(): PendingTab {
  const tab = window.open("", "_blank");
  return {
    go(url) {
      if (tab === null) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      tab.opener = null;
      tab.location.href = url;
    },
    close() {
      tab?.close();
    },
  };
}

export interface MeetPanelProps {
  workspaceId: string;
  channelId: string;
  defaultTitle: string;
  /** Members' addresses offered as attendees. */
  memberEmails: readonly string[];
  /** Starts incremental consent for the account (leaves the page). */
  onAllow: (account: Account) => Promise<void>;
  openTab?: () => PendingTab;
  onDone: () => void;
}

/**
 * The Meet dialog's body: pick one of my Google accounts; if Meet is not allowed for it yet,
 * explain and offer the consent; otherwise "Start now" (posts the link here and opens it) or
 * "Schedule…" (a Calendar event with a Meet link, posted here).
 */
export function MeetPanel({ workspaceId, channelId, defaultTitle, memberEmails, onAllow, openTab = blankTab, onDone }: MeetPanelProps) {
  const trpc = useTRPC();
  const toast = useToast();
  const accounts = useQuery(trpc.accounts.list.queryOptions());
  const google = accounts.data === undefined ? [] : googleAccounts(accounts.data);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [title, setTitle] = useState(defaultTitle);
  const [mode, setMode] = useState<"choose" | "schedule">("choose");
  const [error, setError] = useState<unknown>(null);
  const [allowing, setAllowing] = useState(false);
  // The account picked, else the first with Meet allowed, else the first.
  const chosen = google.find((a) => a.id === chosenId);
  const preferred = google.find((a) => meetEnabled(a));
  const account = chosen !== undefined ? chosen : preferred !== undefined ? preferred : google[0];

  const queryClient = useQueryClient();
  const startNow = useMutation(
    trpc.meet.startNow.mutationOptions({ onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.meet.list.pathKey() }) }),
  );

  if (accounts.isPending) return <p className="text-sm text-muted">Loading your accounts…</p>;
  if (accounts.isError) return <ErrorText>{meetErrorText(accounts.error)}</ErrorText>;
  if (account === undefined) {
    return <p className="text-sm text-muted">Meet needs a Google account. Connect one in Settings → Accounts first.</p>;
  }

  const allow = async () => {
    setAllowing(true);
    try {
      await onAllow(account);
    } catch (e) {
      setAllowing(false);
      setError(e);
    }
  };

  const start = () => {
    setError(null);
    const tab = openTab();
    startNow.mutate(
      { workspaceId, accountId: account.id, title: title.trim(), postTo: { channelId } },
      {
        onSuccess: (out) => {
          tab.go(out.meeting.meetUri);
          toast({ message: "Meeting started; the link is in the conversation." });
          onDone();
        },
        onError: (e) => {
          tab.close();
          setError(e);
        },
      },
    );
  };

  const enabled = meetEnabled(account) && !isScopesMissing(error);

  return (
    <div className="flex flex-col gap-4">
      {google.length > 1 ? (
        <div className="flex flex-col gap-1">
          <label htmlFor="meet-account" className="text-sm font-medium text-text">
            Google account
          </label>
          <select
            id="meet-account"
            value={account.id}
            onChange={(e) => {
              setChosenId(e.target.value);
              setError(null);
            }}
            className="min-h-9 rounded-md border border-border bg-surface px-3 text-sm text-text"
          >
            {google.map((a) => (
              <option key={a.id} value={a.id}>
                {a.address}
                {meetEnabled(a) ? "" : " (Meet not allowed yet)"}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className="text-sm text-muted">
          With <span className="text-text">{account.address}</span>
        </p>
      )}

      {!enabled ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-text">
            Meet is not allowed for {account.address} yet. Google will ask you once to let KitWork+ create meetings and calendar
            events for this account; nothing else changes.
          </p>
          <Button variant="primary" onClick={() => void allow()} disabled={allowing}>
            {allowing ? "Opening Google…" : `Allow Meet for ${account.address}`}
          </Button>
        </div>
      ) : (
        <>
          <TextField label="Title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          {mode === "choose" ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={start} disabled={startNow.isPending || title.trim().length === 0}>
                {startNow.isPending ? "Starting…" : "Start now"}
              </Button>
              <Button onClick={() => setMode("schedule")} disabled={startNow.isPending}>
                Schedule…
              </Button>
            </div>
          ) : (
            <ScheduleForm
              workspaceId={workspaceId}
              channelId={channelId}
              accountId={account.id}
              title={title.trim()}
              memberEmails={memberEmails}
              onError={setError}
              onBack={() => setMode("choose")}
              onDone={onDone}
            />
          )}
        </>
      )}
      {error === null || isScopesMissing(error) ? null : <ErrorText>{meetErrorText(error)}</ErrorText>}
    </div>
  );
}
